import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Heart, Sparkles, Mail } from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [debugInfo, setDebugInfo] = useState({});
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [currentCheckoutId, setCurrentCheckoutId] = useState(null);
  const { cart, clearCart } = useCart();

  useEffect(() => {
    // Get order_id from URL (most reliable method)
    const orderId = searchParams.get('order_id');
    // Also check for checkout_id as fallback
    const checkoutId = searchParams.get('checkout_id') || 
                       searchParams.get('session_id') || 
                       searchParams.get('id');
    
    console.log('[ORDER SUCCESS] URL params - order_id:', orderId, 'checkout_id:', checkoutId);
    
    setDebugInfo({ orderId, checkoutId, source: orderId ? 'order_id' : (checkoutId ? 'checkout_id' : 'none') });
    
    if (orderId) {
      setCurrentOrderId(orderId);
      // Use order_id based lookup (most reliable)
      pollPaymentStatusByOrder(orderId, 0);
    } else if (checkoutId) {
      setCurrentCheckoutId(checkoutId);
      // Fallback to checkout_id
      pollPaymentStatusByCheckout(checkoutId, 0);
    } else {
      // Try localStorage as last resort
      const storedOrderId = localStorage.getItem('pendingOrderId');
      const storedCheckoutId = localStorage.getItem('pendingCheckoutId');
      
      console.log('[ORDER SUCCESS] localStorage - order_id:', storedOrderId, 'checkout_id:', storedCheckoutId);
      setDebugInfo(prev => ({ ...prev, storedOrderId, storedCheckoutId }));
      
      if (storedOrderId) {
        setCurrentOrderId(storedOrderId);
        pollPaymentStatusByOrder(storedOrderId, 0);
      } else if (storedCheckoutId) {
        setCurrentCheckoutId(storedCheckoutId);
        pollPaymentStatusByCheckout(storedCheckoutId, 0);
      } else {
        console.error('[ORDER SUCCESS] No order_id or checkout_id found');
        setPaymentStatus('error');
        setLoading(false);
      }
    }
  }, [searchParams]);

  const pollPaymentStatusByOrder = async (orderId, attempt) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    console.log(`[ORDER SUCCESS] Polling by order_id - Attempt ${attempt + 1}/${maxAttempts}`);

    if (attempt >= maxAttempts) {
      setPaymentStatus('timeout');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/by-order/${orderId}`, {
        timeout: 15000
      });
      const data = response.data;
      
      console.log('[ORDER SUCCESS] Payment status response:', data);
      setDebugInfo(prev => ({ ...prev, lastResponse: data }));
      
      // Store checkout_id if returned
      if (data.checkout_id) {
        setCurrentCheckoutId(data.checkout_id);
      }

      if (data.payment_status === 'paid') {
        console.log('[ORDER SUCCESS] Payment confirmed as PAID');
        setPaymentStatus('success');
        setOrderedProducts([...cart]);
        clearCart();
        localStorage.removeItem('pendingOrderId');
        localStorage.removeItem('pendingCheckoutId');
        // Mark cart session as completed for abandoned cart tracking
        if (data.customer_email) {
          axios.post(`${API}/cart/session/complete?email=${encodeURIComponent(data.customer_email)}`).catch(() => {});
        }
        setLoading(false);
      } else if (data.payment_status === 'failed') {
        console.log('[ORDER SUCCESS] Payment failed');
        setPaymentStatus('failed');
        localStorage.removeItem('pendingOrderId');
        localStorage.removeItem('pendingCheckoutId');
        setLoading(false);
      } else {
        console.log(`[ORDER SUCCESS] Payment status: ${data.payment_status} - polling again...`);
        setTimeout(() => pollPaymentStatusByOrder(orderId, attempt + 1), pollInterval);
      }
    } catch (error) {
      console.error('[ORDER SUCCESS ERROR]', error);
      if (attempt < 3) {
        setTimeout(() => pollPaymentStatusByOrder(orderId, attempt + 1), pollInterval);
      } else {
        setPaymentStatus('error');
        setLoading(false);
      }
    }
  };

  const pollPaymentStatusByCheckout = async (checkoutId, attempt) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    console.log(`[ORDER SUCCESS] Polling by checkout_id - Attempt ${attempt + 1}/${maxAttempts}`);

    if (attempt >= maxAttempts) {
      setPaymentStatus('timeout');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/yoco/status/${checkoutId}`, {
        timeout: 15000
      });
      const data = response.data;
      
      console.log('[ORDER SUCCESS] Payment status response:', data);
      setDebugInfo(prev => ({ ...prev, lastResponse: data }));

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setOrderedProducts([...cart]);
        clearCart();
        localStorage.removeItem('pendingOrderId');
        localStorage.removeItem('pendingCheckoutId');
        setLoading(false);
      } else if (data.payment_status === 'failed') {
        setPaymentStatus('failed');
        setLoading(false);
      } else {
        setTimeout(() => pollPaymentStatusByCheckout(checkoutId, attempt + 1), pollInterval);
      }
    } catch (error) {
      console.error('[ORDER SUCCESS ERROR]', error);
      if (attempt < 3) {
        setTimeout(() => pollPaymentStatusByCheckout(checkoutId, attempt + 1), pollInterval);
      } else {
        setPaymentStatus('error');
        setLoading(false);
      }
    }
  };

  // Get product-specific thank you messages
  const getThankYouMessages = () => {
    const messages = [];
    
    orderedProducts.forEach(item => {
      if (item.name === "Aqua Wash") {
        messages.push({
          product: "Aqua Wash",
          line1: "Your comfort matters.",
          line2: "This is care designed to keep your skin soft, supported, and at ease."
        });
      } else if (item.name === "Hydra Oil") {
        messages.push({
          product: "Hydra Oil",
          line1: "Your comfort matters.",
          line2: "This is care designed to keep your skin soft, supported, and at ease."
        });
      } else if (item.name === "Boric Pops") {
        messages.push({
          product: "Boric Pops",
          line1: "Your body's signals deserve attention.",
          line2: "This is support you can reach for when you need it."
        });
      } else if (item.name === "Detox Pearls") {
        messages.push({
          product: "Detox Pearls",
          line1: "You listened to your body.",
          line2: "This is a moment of pause, intention, and care."
        });
      } else if (item.name === "Triple Treat Trio") {
        messages.push({
          product: "Triple Treat Trio",
          line1: "Your comfort matters.",
          line2: "This complete routine is designed to support your body with intention and care."
        });
      }
    });
    
    // Remove duplicates
    const uniqueMessages = messages.filter((msg, index, self) =>
      index === self.findIndex((m) => m.product === msg.product)
    );
    
    return uniqueMessages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-6">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-perfect-peach/20 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-perfect-peach border-t-transparent rounded-full animate-spin absolute top-0"></div>
            </div>
          </div>
          <p className="text-african-earth text-lg mb-2">Confirming your payment...</p>
          <p className="text-african-earth/60 text-sm">This will just take a moment</p>
          
          {/* Test Mode Helper */}
          {(currentCheckoutId || currentOrderId) && (
            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-xs text-yellow-800 mb-3">
                <strong>Test Mode:</strong> If you're testing and payment is stuck, click below:
              </p>
              <button
                onClick={async () => {
                  try {
                    console.log('[TEST MODE] Manually completing payment...');
                    if (currentCheckoutId) {
                      await axios.post(`${API}/test/complete-payment/${currentCheckoutId}`);
                    }
                    console.log('[TEST MODE] Payment completed, refreshing status...');
                    if (currentOrderId) {
                      pollPaymentStatusByOrder(currentOrderId, 0);
                    } else if (currentCheckoutId) {
                      pollPaymentStatusByCheckout(currentCheckoutId, 0);
                    }
                  } catch (error) {
                    console.error('[TEST MODE ERROR]', error);
                  }
                }}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-yellow-700"
              >
                Complete Test Payment
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed' || paymentStatus === 'error' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-bone-white">
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="bg-white rounded-3xl p-12 shadow-soft text-center">
            <div className="w-20 h-20 bg-african-earth/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl text-african-earth">·</span>
            </div>
            <h1 className="font-playfair text-3xl font-semibold text-[#2D2420] mb-4">
              Let's Try That Again
            </h1>
            <p className="text-african-earth text-lg leading-relaxed mb-8">
              {paymentStatus === 'timeout' 
                ? "We're having trouble confirming your payment. If you completed the payment, please check your email for confirmation or contact us."
                : "We weren't able to confirm your payment. No worries—your card hasn't been charged. Would you like to try again or explore other payment options?"
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cart"
                className="bg-perfect-peach text-white px-8 py-4 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300"
                data-testid="back-to-cart-button"
              >
                Return to Cart
              </Link>
              <Link
                to="/banking-details"
                className="bg-transparent border-2 border-african-earth text-african-earth px-8 py-4 rounded-full font-medium hover:bg-african-earth hover:text-white transition-all duration-300"
              >
                View Bank Transfer Option
              </Link>
            </div>
            
            {/* Debug info for troubleshooting */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl text-left">
              <p className="text-xs text-gray-500 mb-2">Debug Info (for support):</p>
              <p className="text-xs font-mono text-gray-600">Status: {paymentStatus}</p>
              <p className="text-xs font-mono text-gray-600">Order ID: {debugInfo.orderId || 'none'}</p>
              <p className="text-xs font-mono text-gray-600">Checkout ID: {debugInfo.checkoutId || 'none'}</p>
              {debugInfo.lastResponse && (
                <p className="text-xs font-mono text-gray-600">Last Response: {JSON.stringify(debugInfo.lastResponse)}</p>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // No payment info found at all
  if (!loading && paymentStatus === null) {
    return (
      <div className="min-h-screen bg-bone-white">
        <Navigation />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="bg-white rounded-3xl p-12 shadow-soft text-center">
            <h1 className="font-playfair text-3xl font-semibold text-[#2D2420] mb-4">
              Order Not Found
            </h1>
            <p className="text-african-earth text-lg leading-relaxed mb-8">
              We couldn't find your order information. If you just completed a payment, please check your email for confirmation.
            </p>
            <Link
              to="/products"
              className="bg-perfect-peach text-white px-8 py-4 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        {/* Main Success Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-perfect-peach/10 rounded-full mb-6">
            <Heart className="w-12 h-12 text-perfect-peach" />
          </div>
          
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold text-[#2D2420] mb-4" data-testid="success-heading">
            Your Routine Begins Here
          </h1>
          
          <p className="text-african-earth text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
            Thank you for choosing intentional, informed intimate care.
          </p>
        </div>

        {/* Product-Specific Thank You Messages */}
        {getThankYouMessages().length > 0 && (
          <div className="mb-12">
            <div className="space-y-6">
              {getThankYouMessages().map((message, index) => (
                <div 
                  key={index}
                  className="bg-perfect-peach/5 border-l-4 border-perfect-peach rounded-r-3xl p-8"
                >
                  <p className="text-[#2D2420] text-lg font-medium mb-3">
                    {message.line1}
                  </p>
                  <p className="text-african-earth leading-relaxed">
                    {message.line2}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What Happens Next */}
        <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-soft mb-8">
          <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-6 text-center">
            What Happens Next
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-perfect-peach/10 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-perfect-peach" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-[#2D2420] mb-2 text-lg">Check Your Email</h3>
                <p className="text-african-earth leading-relaxed">
                  You'll receive an order confirmation shortly with all the details. 
                  Keep an eye on your inbox—we'll keep you updated every step of the way.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-royal-gold/10 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-royal-gold" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-[#2D2420] mb-2 text-lg">We Prepare Your Order</h3>
                <p className="text-african-earth leading-relaxed">
                  Within 1-2 business days, your products will be carefully packaged 
                  and ready to begin their journey to you.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-african-earth/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-african-earth" />
                </div>
              </div>
              <div>
                <h3 className="font-medium text-[#2D2420] mb-2 text-lg">Arrive at Your Doorstep</h3>
                <p className="text-african-earth leading-relaxed">
                  Your wellness essentials will arrive in 3-5 business days. 
                  When they do, take a moment to honor this act of self-care.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Care Guide */}
        <div className="bg-gradient-to-br from-perfect-peach/5 to-royal-gold/5 rounded-3xl p-8 sm:p-10 mb-8 border border-perfect-peach/10">
          <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-4 text-center">
            A Word on Using Your Products
          </h2>
          <p className="text-african-earth leading-relaxed text-center max-w-2xl mx-auto mb-6">
            Your body knows itself. These products are here to support—not override—your natural balance. 
            Start slowly, listen to how you feel, and trust your intuition. 
          </p>
          <p className="text-african-earth/80 text-sm text-center italic">
            "Work with your body, not against it."
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/products"
            className="bg-perfect-peach text-white px-8 py-4 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300 text-center"
            data-testid="continue-shopping-button"
          >
            Continue Exploring
          </Link>
          <Link
            to="/about"
            className="bg-transparent border-2 border-african-earth text-african-earth px-8 py-4 rounded-full font-medium hover:bg-african-earth hover:text-white transition-all duration-300 text-center"
          >
            Learn Our Philosophy
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderSuccessPage;